import { QueryClient, UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { travelRequestKey } from '@/module/Travel/Request/model/TravelRequestQuery';

import { Id, getQueryKey, useAppNotification } from '@feature/utils';

import * as api from './TravelApprovalApi';
import {
  TravelApprovalParams,
  TravelApprovalQueue,
  TravelDeanMailRouting,
  TravelDeanMailRoutingRequest,
  TravelDecisionRequest,
  TravelReturnRequest,
} from './TravelApprovalTypes';

export const travelApprovalKey = getQueryKey('travelApproval');

const deanMailRoutingKey = travelApprovalKey.as(['deanMailRouting']);

export function getTravelDeanMailRoutingOptions(): UseSuspenseQueryOptions<TravelDeanMailRouting> {
  return {
    queryKey: deanMailRoutingKey,
    queryFn: api.getDeanMailRouting,
  };
}

export function useTravelDeanMailRoutingEdit() {
  const queryClient = useQueryClient();
  const notification = useAppNotification();
  return useMutation({
    mutationFn: (request: TravelDeanMailRoutingRequest) => api.saveDeanMailRouting(request),
    onSuccess: routing => queryClient.setQueryData(deanMailRoutingKey, routing),
    // Hook-level, so the error still shows when the caller unmounted before the answer.
    onError: error => {
      notification.error(error);
      return queryClient.invalidateQueries({ queryKey: deanMailRoutingKey });
    },
  });
}

export function getTravelApprovalPagedOptions(
  params: TravelApprovalParams,
): UseSuspenseQueryOptions<TravelApprovalQueue> {
  return {
    queryKey: travelApprovalKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

function invalidateDecision(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: travelApprovalKey.root() }),
    queryClient.invalidateQueries({ queryKey: travelRequestKey.root() }),
  ]);
}

// A refused decision means the cached status is stale; unawaited, so the caller reports the error before the refetch unmounts it.
function refreshRefusedDecision(queryClient: QueryClient) {
  void invalidateDecision(queryClient);
}

export function useTravelApprovalApprove(requestId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request?: TravelDecisionRequest) => api.approve(requestId, request),
    onSuccess: () => invalidateDecision(queryClient),
    onError: () => refreshRefusedDecision(queryClient),
  });
}

export function useTravelApprovalBoardSent(requestId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.markBoardSent(requestId),
    onSuccess: () => invalidateDecision(queryClient),
    onError: () => refreshRefusedDecision(queryClient),
  });
}

export function useTravelApprovalReturn(requestId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: TravelReturnRequest) => api.returnForChanges(requestId, request),
    onSuccess: () => invalidateDecision(queryClient),
    onError: () => refreshRefusedDecision(queryClient),
  });
}

export function useTravelApprovalReject(requestId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: TravelDecisionRequest) => api.reject(requestId, request),
    onSuccess: () => invalidateDecision(queryClient),
    onError: () => refreshRefusedDecision(queryClient),
  });
}
